import React, { useState, useEffect, useRef, useCallback } from 'react';
import DailyIframe from '@daily-co/daily-js';
import { 
  Video, 
  VideoOff, 
  Mic, 
  MicOff, 
  PhoneOff, 
  Users,
  Maximize2,
  Minimize2,
  Camera,
  MessageSquare,
  Hand,
  Settings,
  Volume2,
  VolumeX,
  RotateCcw,
  Wifi,
  WifiOff,
  Signal,
  SignalLow,
  SignalMedium,
  SignalHigh,
  AlertTriangle
} from 'lucide-react';
import Theta360Viewer from './Theta360Viewer';

/**
 * Daily360MeetingRoom - Custom Daily.co meeting room with 360° THETA support
 * 
 * Features:
 * - Host can share THETA 360° camera stream
 * - Remote participants see interactive 360° view (navigate, zoom)
 * - All participants visible via webcam/smartphone camera
 * - Bidirectional audio
 * - Connection quality indicator
 */
const Daily360MeetingRoom = ({
  roomUrl,
  token,
  userName,
  is360Room = false,
  isHost = false,
  thetaStream = null,  // THETA stream from host
  onLeave,
  onError
}) => {
  // Daily.co call object
  const [callObject, setCallObject] = useState(null);
  const [callState, setCallState] = useState('idle'); // idle, joining, joined, left, error
  
  // Participants
  const [participants, setParticipants] = useState({});
  const [localParticipant, setLocalParticipant] = useState(null);
  const [hostParticipant, setHostParticipant] = useState(null);
  
  // Local controls
  const [isCameraOn, setIsCameraOn] = useState(true);
  const [isMicOn, setIsMicOn] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // 360° stream from host
  const [theta360Stream, setTheta360Stream] = useState(null);
  
  // Connection quality
  const [networkQuality, setNetworkQuality] = useState(null);
  const [networkStats, setNetworkStats] = useState(null);
  const networkStatsIntervalRef = useRef(null);
  
  // Container ref for fullscreen
  const containerRef = useRef(null);

  // Ref to track if call object was created
  const callObjectRef = useRef(null);
  const mountedRef = useRef(true);
  const hasInitializedRef = useRef(false);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // Cleanup call object on unmount
      if (callObjectRef.current) {
        try {
          callObjectRef.current.destroy();
        } catch (e) {
          console.log('Error destroying call object on unmount:', e);
        }
        callObjectRef.current = null;
      }
    };
  }, []);

  // Initialize Daily.co call object - run ONCE
  useEffect(() => {
    // Validate required parameters
    if (!roomUrl) {
      console.log('Daily360MeetingRoom: No roomUrl');
      return;
    }
    if (!token || typeof token !== 'string') {
      console.log('Daily360MeetingRoom: Waiting for valid token...');
      return;
    }
    
    // Critical: Only initialize once using a ref
    if (hasInitializedRef.current) {
      console.log('Daily360MeetingRoom: Already initialized, skipping');
      return;
    }

    hasInitializedRef.current = true;
    console.log('Daily360MeetingRoom: Starting initialization...');

    const initializeDaily = async () => {
      if (!mountedRef.current) return;
      
      try {
        console.log('Daily360MeetingRoom: Creating call object...');
        const daily = DailyIframe.createCallObject({
          url: roomUrl,
          token: token,
          subscribeToTracksAutomatically: true,
          dailyConfig: {
            experimentalChromeVideoMuteLightOff: true
          }
        });

        if (!mountedRef.current) {
          daily.destroy();
          return;
        }

        callObjectRef.current = daily;
        
        // Event handlers
        daily.on('joining-meeting', () => {
          console.log('Daily360MeetingRoom: Joining meeting event');
          if (mountedRef.current) setCallState('joining');
        });
        daily.on('joined-meeting', handleJoinedMeeting);
        daily.on('left-meeting', handleLeftMeeting);
        daily.on('participant-joined', handleParticipantUpdate);
        daily.on('participant-updated', handleParticipantUpdate);
        daily.on('participant-left', handleParticipantLeft);
        daily.on('track-started', handleTrackStarted);
        daily.on('track-stopped', handleTrackStopped);
        daily.on('error', handleError);
        
        if (mountedRef.current) {
          setCallObject(daily);
          
          // Join meeting immediately
          console.log('Daily360MeetingRoom: Joining meeting with userName:', userName);
          setCallState('joining');
          await daily.join({ userName });
          console.log('Daily360MeetingRoom: Join completed');
        }
      } catch (err) {
        console.error('Daily360MeetingRoom: Error:', err);
        hasInitializedRef.current = false; // Allow retry
        if (mountedRef.current) {
          setCallState('error');
          onError?.('Erreur lors de l\'initialisation: ' + (err.message || err));
        }
      }
    };

    initializeDaily();

    return () => {
      // Cleanup is handled in the unmount effect
    };
  }, [roomUrl, token]); // Only depend on roomUrl and token

  // Join meeting when call object is ready (backup, main join is in initialization)
  useEffect(() => {
    if (callObject && callState === 'idle' && !hasInitializedRef.current) {
      console.log('Daily360MeetingRoom: Call object ready, triggering backup join...');
      joinMeeting();
    }
  }, [callObject, callState]);

  // Share THETA stream if host
  useEffect(() => {
    if (callObject && isHost && thetaStream && callState === 'joined') {
      shareThetaStream();
    }
  }, [callObject, isHost, thetaStream, callState]);

  // Monitor network quality
  useEffect(() => {
    if (!callObject || callState !== 'joined') {
      return;
    }

    // Handle network quality changes from Daily.co
    const handleNetworkQualityChange = (event) => {
      console.log('Network quality changed:', event);
      if (event.threshold) {
        setNetworkQuality(event.threshold);
      }
    };

    // Subscribe to network quality events
    callObject.on('network-quality-change', handleNetworkQualityChange);

    // Poll for detailed network stats every 3 seconds
    const getNetworkStats = async () => {
      try {
        const stats = await callObject.getNetworkStats();
        if (stats && mountedRef.current) {
          setNetworkStats(stats);
          
          // Calculate quality from stats if no threshold available
          if (!networkQuality && stats.stats) {
            const { latest } = stats.stats;
            if (latest) {
              // Estimate quality based on packet loss and latency
              const videoRecvPacketLoss = latest.videoRecvPacketLoss || 0;
              const audioRecvPacketLoss = latest.audioRecvPacketLoss || 0;
              const avgPacketLoss = (videoRecvPacketLoss + audioRecvPacketLoss) / 2;
              
              let quality = 'good';
              if (avgPacketLoss > 10) quality = 'bad';
              else if (avgPacketLoss > 3) quality = 'low';
              
              setNetworkQuality(quality);
            }
          }
        }
      } catch (err) {
        console.log('Error getting network stats:', err);
      }
    };

    // Initial stats fetch
    getNetworkStats();
    
    // Poll every 3 seconds
    networkStatsIntervalRef.current = setInterval(getNetworkStats, 3000);

    return () => {
      callObject.off('network-quality-change', handleNetworkQualityChange);
      if (networkStatsIntervalRef.current) {
        clearInterval(networkStatsIntervalRef.current);
      }
    };
  }, [callObject, callState]);

  const joinMeeting = async () => {
    if (!callObject) return;
    
    try {
      console.log('Daily360MeetingRoom: Joining meeting with userName:', userName);
      setCallState('joining');
      await callObject.join({ userName });
      console.log('Daily360MeetingRoom: Successfully joined meeting');
    } catch (err) {
      console.error('Error joining meeting:', err);
      // Check if it's a network error or token issue
      const errorMsg = err.message || err.toString();
      if (errorMsg.includes('network') || errorMsg.includes('fetch')) {
        // Retry once for network errors
        console.log('Network error, retrying join...');
        try {
          await new Promise(resolve => setTimeout(resolve, 2000));
          await callObject.join({ userName });
          return;
        } catch (retryErr) {
          console.error('Retry failed:', retryErr);
        }
      }
      setCallState('error');
      onError?.('Erreur lors de la connexion à la réunion: ' + errorMsg);
    }
  };

  const shareThetaStream = async () => {
    if (!callObject || !thetaStream) return;
    
    try {
      // Get video track from THETA stream
      const videoTrack = thetaStream.getVideoTracks()[0];
      if (videoTrack) {
        // Use custom track for THETA
        await callObject.setInputDevicesAsync({
          videoSource: videoTrack
        });
        console.log('THETA 360° stream shared to meeting');
      }
    } catch (err) {
      console.error('Error sharing THETA stream:', err);
    }
  };

  const handleJoinedMeeting = (event) => {
    setCallState('joined');
    updateParticipants();
  };

  const handleLeftMeeting = () => {
    setCallState('left');
    onLeave?.();
  };

  const handleParticipantUpdate = (event) => {
    updateParticipants();
  };

  const handleParticipantLeft = (event) => {
    updateParticipants();
  };

  const handleTrackStarted = (event) => {
    updateParticipants();
    
    // Check if this is the host's video track (potential THETA 360°)
    if (is360Room && event.participant && !event.participant.local) {
      checkFor360Stream(event.participant);
    }
  };

  const handleTrackStopped = (event) => {
    updateParticipants();
  };

  const handleError = (event) => {
    console.error('Daily.co error:', event);
    
    // Don't set error state for non-critical errors
    const errorMsg = event.errorMsg || event.error?.msg || '';
    const isFatal = event.action === 'error' && 
      (errorMsg.includes('not-allowed') || errorMsg.includes('invalid') || errorMsg.includes('expired'));
    
    if (isFatal) {
      setCallState('error');
      onError?.(errorMsg || 'Erreur de connexion');
    } else {
      // Log but don't block - might be a recoverable error
      console.warn('Daily.co non-fatal error:', event);
    }
  };

  // Retry connection
  const retryConnection = () => {
    console.log('Retrying connection...');
    // Reset initialization flag and reload
    hasInitializedRef.current = false;
    window.location.reload();
  };

  const updateParticipants = () => {
    if (!callObject) return;
    
    const allParticipants = callObject.participants();
    setParticipants(allParticipants);
    
    // Find local participant
    const local = allParticipants.local;
    setLocalParticipant(local);
    
    // Find host (first non-local participant with video, or owner)
    const remoteParticipants = Object.entries(allParticipants)
      .filter(([id]) => id !== 'local')
      .map(([id, p]) => ({ id, ...p }));
    
    if (remoteParticipants.length > 0) {
      // For 360° rooms, the first participant with video is likely the host with THETA
      const potentialHost = remoteParticipants.find(p => p.video) || remoteParticipants[0];
      setHostParticipant(potentialHost);
      
      if (is360Room && potentialHost?.tracks?.video?.persistentTrack) {
        // Create stream from host's video track for 360° viewer
        const hostVideoTrack = potentialHost.tracks.video.persistentTrack;
        const stream = new MediaStream([hostVideoTrack]);
        setTheta360Stream(stream);
      }
    }
  };

  const checkFor360Stream = (participant) => {
    if (participant.tracks?.video?.persistentTrack) {
      const videoTrack = participant.tracks.video.persistentTrack;
      const stream = new MediaStream([videoTrack]);
      setTheta360Stream(stream);
    }
  };

  const toggleCamera = () => {
    if (!callObject) return;
    callObject.setLocalVideo(!isCameraOn);
    setIsCameraOn(!isCameraOn);
  };

  const toggleMic = () => {
    if (!callObject) return;
    callObject.setLocalAudio(!isMicOn);
    setIsMicOn(!isMicOn);
  };

  const leaveMeeting = () => {
    if (!callObject) return;
    callObject.leave();
  };

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await containerRef.current?.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (err) {
      // Ignore fullscreen errors (user may have exited manually)
      console.log('Fullscreen toggle error:', err.message);
      setIsFullscreen(!!document.fullscreenElement);
    }
  };

  // Get remote participants (excluding local)
  const remoteParticipants = Object.entries(participants)
    .filter(([id]) => id !== 'local')
    .map(([id, p]) => ({ id, ...p }));

  // Network quality indicator component
  const NetworkQualityIndicator = () => {
    // Always show for joined state - even if alone
    if (callState !== 'joined') {
      return null;
    }
    
    // Show connecting/measuring state if no quality data yet
    if (!networkQuality && !networkStats) {
      return (
        <div 
          className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-gray-700/50"
          data-testid="network-quality-indicator"
          title="Mesure de la qualité de connexion en cours..."
        >
          <Wifi size={14} className="text-gray-400 animate-pulse" />
          <span className="text-xs text-gray-400">Connexion...</span>
        </div>
      );
    }

    // Determine quality level
    let quality = networkQuality || 'good';
    let qualityLabel = 'Excellente';
    let qualityColor = 'text-green-400';
    let bgColor = 'bg-green-500/20';
    let IconComponent = SignalHigh;
    let bars = 4;

    if (quality === 'low' || quality === 'poor') {
      qualityLabel = 'Moyenne';
      qualityColor = 'text-yellow-400';
      bgColor = 'bg-yellow-500/20';
      IconComponent = SignalMedium;
      bars = 2;
    } else if (quality === 'bad' || quality === 'very-low') {
      qualityLabel = 'Faible';
      qualityColor = 'text-red-400';
      bgColor = 'bg-red-500/20';
      IconComponent = SignalLow;
      bars = 1;
    } else if (quality === 'good') {
      qualityLabel = 'Bonne';
      qualityColor = 'text-green-400';
      bgColor = 'bg-green-500/20';
      IconComponent = SignalHigh;
      bars = 3;
    }

    // Extract detailed stats if available
    let latency = null;
    let packetLoss = null;
    if (networkStats?.stats?.latest) {
      const { latest } = networkStats.stats;
      latency = latest.videoRecvBitsPerSecond 
        ? Math.round(latest.videoRecvBitsPerSecond / 1000) 
        : null;
      packetLoss = latest.videoRecvPacketLoss 
        ? latest.videoRecvPacketLoss.toFixed(1) 
        : null;
    }

    return (
      <div 
        className={`flex items-center gap-1.5 px-2 py-1 rounded-lg ${bgColor} cursor-pointer group relative`}
        data-testid="network-quality-indicator"
      >
        {/* Signal bars */}
        <div className="flex items-end gap-0.5 h-4">
          {[1, 2, 3, 4].map((bar) => (
            <div
              key={bar}
              className={`w-1 rounded-sm transition-all ${
                bar <= bars 
                  ? qualityColor.replace('text-', 'bg-') 
                  : 'bg-gray-600'
              }`}
              style={{ height: `${bar * 3 + 2}px` }}
            />
          ))}
        </div>
        
        {/* Quality label */}
        <span className={`text-xs font-medium ${qualityColor}`}>
          {qualityLabel}
        </span>

        {/* Tooltip with detailed stats */}
        <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block z-50">
          <div className="bg-gray-900 text-white text-xs rounded-lg p-3 shadow-xl border border-gray-700 min-w-[180px]">
            <div className="font-semibold mb-2 flex items-center gap-2">
              <Wifi size={14} className={qualityColor} />
              Qualité de connexion
            </div>
            <div className="space-y-1.5">
              <div className="flex justify-between">
                <span className="text-gray-400">État :</span>
                <span className={qualityColor}>{qualityLabel}</span>
              </div>
              {latency && (
                <div className="flex justify-between">
                  <span className="text-gray-400">Débit vidéo :</span>
                  <span>{latency} kbps</span>
                </div>
              )}
              {packetLoss && (
                <div className="flex justify-between">
                  <span className="text-gray-400">Perte paquets :</span>
                  <span className={parseFloat(packetLoss) > 3 ? 'text-red-400' : ''}>{packetLoss}%</span>
                </div>
              )}
            </div>
            {quality === 'bad' && (
              <div className="mt-2 pt-2 border-t border-gray-700 text-yellow-400 text-[10px] flex items-start gap-1">
                <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" />
                <span>Connexion instable. Fermez d'autres applications utilisant internet.</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render waiting for token state
  if (!token || typeof token !== 'string') {
    return (
      <div className="flex items-center justify-center h-full min-h-[300px] bg-gray-900 rounded-xl">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00D3A8] mx-auto mb-4"></div>
          <p>Préparation de la connexion...</p>
        </div>
      </div>
    );
  }

  // Render loading state
  if (callState === 'idle' || callState === 'joining') {
    return (
      <div className="flex items-center justify-center h-full min-h-[300px] bg-gray-900 rounded-xl">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00D3A8] mx-auto mb-4"></div>
          <p>Connexion à la salle de réunion...</p>
        </div>
      </div>
    );
  }

  // Render error state
  if (callState === 'error') {
    return (
      <div className="flex items-center justify-center h-full min-h-[300px] bg-gray-900 rounded-xl">
        <div className="text-center text-white">
          <VideoOff size={48} className="mx-auto mb-4 text-red-400" />
          <p className="text-lg font-medium">Erreur de connexion</p>
          <button
            onClick={retryConnection}
            className="mt-4 px-4 py-2 bg-[#00D3A8] rounded-lg hover:bg-[#00B894]"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className="bg-gray-900 rounded-xl overflow-hidden h-full flex flex-col"
      data-testid="daily-360-meeting-room"
    >
      {/* Main video area */}
      <div className="relative flex-1 min-h-[250px]">
        {/* 360° View for remote participants or Host preview */}
        {is360Room && theta360Stream ? (
          <div className="absolute inset-0">
            <Theta360Viewer
              stream={theta360Stream}
              isActive={true}
              className="w-full h-full"
              showControls={true}
            />
            {/* 360° badge */}
            <div className="absolute top-4 left-4 flex items-center gap-2 z-10">
              <span className="px-3 py-1 bg-[#00D3A8] text-white text-sm font-medium rounded-full flex items-center gap-1">
                <Camera size={14} />
                Vue 360° Interactive
              </span>
            </div>
          </div>
        ) : (
          // Standard video grid for non-360 rooms
          <div className="absolute inset-0 flex items-center justify-center">
            {remoteParticipants.length > 0 ? (
              <div className={`grid gap-2 p-4 w-full h-full ${
                remoteParticipants.length === 1 ? 'grid-cols-1' :
                remoteParticipants.length <= 4 ? 'grid-cols-2' :
                'grid-cols-3'
              }`}>
                {remoteParticipants.map((participant) => (
                  <ParticipantTile
                    key={participant.id}
                    participant={participant}
                    isLarge={remoteParticipants.length === 1}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center text-white">
                <Users size={48} className="mx-auto mb-4 text-gray-500" />
                <p>En attente des autres participants...</p>
              </div>
            )}
          </div>
        )}

        {/* Participants sidebar for 360° mode */}
        {is360Room && remoteParticipants.length > 0 && (
          <div className="absolute top-4 right-4 w-48 space-y-2 z-10">
            <div className="bg-black/60 rounded-lg p-2">
              <p className="text-white text-xs font-medium mb-2 flex items-center gap-1">
                <Users size={12} />
                Participants ({remoteParticipants.length + 1})
              </p>
              <div className="space-y-1 max-h-60 overflow-y-auto">
                {/* Local participant thumbnail */}
                {localParticipant && (
                  <ParticipantMini 
                    participant={localParticipant} 
                    isLocal={true}
                    label="Vous"
                  />
                )}
                {/* Remote participants thumbnails */}
                {remoteParticipants.map((p) => (
                  <ParticipantMini 
                    key={p.id} 
                    participant={p}
                    label={p.user_name || 'Participant'}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Local video preview (small) */}
        {localParticipant && !is360Room && (
          <div className="absolute bottom-4 right-4 w-40 h-28 bg-gray-800 rounded-lg overflow-hidden border-2 border-gray-700">
            <ParticipantTile participant={localParticipant} isLocal={true} />
            <div className="absolute bottom-1 left-1 text-white text-xs bg-black/50 px-1 rounded">
              Vous
            </div>
          </div>
        )}

        {/* Fullscreen toggle */}
        <button
          onClick={toggleFullscreen}
          className="absolute top-4 right-4 p-2 bg-black/50 hover:bg-black/70 text-white rounded-lg transition-colors z-20"
        >
          {isFullscreen ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
        </button>
      </div>

      {/* Controls bar */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-800">
        {/* Left side - Network quality */}
        <div className="flex items-center gap-2">
          <NetworkQualityIndicator />
        </div>

        {/* Center - Main controls */}
        <div className="flex items-center gap-3">
          {/* Camera toggle */}
          <button
            onClick={toggleCamera}
            className={`p-3 rounded-full transition-colors ${
              isCameraOn 
                ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                : 'bg-red-500 hover:bg-red-600 text-white'
            }`}
            title={isCameraOn ? 'Désactiver la caméra' : 'Activer la caméra'}
          >
            {isCameraOn ? <Video size={20} /> : <VideoOff size={20} />}
          </button>

          {/* Mic toggle */}
          <button
            onClick={toggleMic}
            className={`p-3 rounded-full transition-colors ${
              isMicOn 
                ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                : 'bg-red-500 hover:bg-red-600 text-white'
            }`}
            title={isMicOn ? 'Désactiver le micro' : 'Activer le micro'}
          >
            {isMicOn ? <Mic size={20} /> : <MicOff size={20} />}
          </button>

          {/* Leave button */}
          <button
            onClick={leaveMeeting}
            className="p-3 bg-red-500 hover:bg-red-600 text-white rounded-full transition-colors"
            title="Quitter la réunion"
          >
            <PhoneOff size={20} />
          </button>
        </div>

        {/* Right side - Participant count */}
        <div className="flex items-center gap-2 text-white">
          <Users size={18} />
          <span className="text-sm font-medium">{Object.keys(participants).length}</span>
        </div>
      </div>
    </div>
  );
};

/**
 * ParticipantTile - Renders a participant's video
 */
const ParticipantTile = ({ participant, isLocal = false, isLarge = false }) => {
  const videoRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => {
    const videoTrack = participant?.tracks?.video?.persistentTrack;
    if (videoRef.current && videoTrack) {
      videoRef.current.srcObject = new MediaStream([videoTrack]);
    }
  }, [participant?.tracks?.video?.persistentTrack]);

  useEffect(() => {
    const audioTrack = participant?.tracks?.audio?.persistentTrack;
    if (audioRef.current && audioTrack && !isLocal) {
      audioRef.current.srcObject = new MediaStream([audioTrack]);
    }
  }, [participant?.tracks?.audio?.persistentTrack, isLocal]);

  const hasVideo = participant?.video;
  const userName = participant?.user_name || (isLocal ? 'Vous' : 'Participant');

  return (
    <div className={`relative bg-gray-800 rounded-lg overflow-hidden ${isLarge ? 'h-full' : 'aspect-video'}`}>
      {hasVideo ? (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted={isLocal}
          className={`w-full h-full object-cover ${isLocal ? 'transform scale-x-[-1]' : ''}`}
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-700">
          <div className="w-16 h-16 rounded-full bg-gray-600 flex items-center justify-center text-white text-2xl font-bold">
            {userName.charAt(0).toUpperCase()}
          </div>
        </div>
      )}
      
      {/* Name label */}
      <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/60 rounded text-white text-sm">
        {userName}
        {!participant?.audio && <MicOff size={12} className="inline ml-1 text-red-400" />}
      </div>

      {/* Audio element (hidden) */}
      {!isLocal && <audio ref={audioRef} autoPlay />}
    </div>
  );
};

/**
 * ParticipantMini - Small participant thumbnail for 360° mode sidebar
 */
const ParticipantMini = ({ participant, isLocal = false, label }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    const videoTrack = participant?.tracks?.video?.persistentTrack;
    if (videoRef.current && videoTrack) {
      videoRef.current.srcObject = new MediaStream([videoTrack]);
    }
  }, [participant?.tracks?.video?.persistentTrack]);

  const hasVideo = participant?.video;

  return (
    <div className="flex items-center gap-2 p-1 bg-gray-800/50 rounded">
      <div className="w-10 h-8 rounded overflow-hidden bg-gray-700 flex-shrink-0">
        {hasVideo ? (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className={`w-full h-full object-cover ${isLocal ? 'transform scale-x-[-1]' : ''}`}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white text-xs">
            {label?.charAt(0).toUpperCase()}
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white text-xs truncate">{label}</p>
        <div className="flex items-center gap-1">
          {participant?.audio ? (
            <Mic size={10} className="text-green-400" />
          ) : (
            <MicOff size={10} className="text-red-400" />
          )}
          {hasVideo ? (
            <Video size={10} className="text-green-400" />
          ) : (
            <VideoOff size={10} className="text-red-400" />
          )}
        </div>
      </div>
    </div>
  );
};

export default Daily360MeetingRoom;
